/**
 * GraphQL queries and hooks — technology pillar.
 * Split from graphql/queries.ts (issue #1409).
 */

import { gql } from '@apollo/client';
import { useQuery, type QueryResult } from '@apollo/client/react';
import type {
  Technology,
  TechnologyConnection,
  TechnologyFilter,
  TechnologyVersion,
  TechnologyVersionStatus,
} from '../types/technology';

export const TECHNOLOGY_FIELDS = gql`
  fragment TechnologyFields on Technology {
    entityId
    entityType
    name
    description
    productFamilyId
    version
    lifecycleStage
    supplierId
    supplierType
    licenseType
    supportEndDate
    securityRating
    replacesId
    replacedById
    tags
    notes
    _schemaVersion
    _createdAt
    _createdBy
    _updatedAt
    _updatedBy
    _deletedAt
  }
`;

/** Fields for a TechnologyVersion record (issue #1283). */
export const TECHNOLOGY_VERSION_FIELDS = gql`
  fragment TechnologyVersionFields on TechnologyVersion {
    entityId
    entityType
    technologyId
    versionLabel
    status
    releaseDate
    notes
    _schemaVersion
    _createdAt
    _createdBy
    _updatedAt
    _updatedBy
    _deletedAt
  }
`;

export const GET_TECHNOLOGY = gql`
  ${TECHNOLOGY_FIELDS}
  ${TECHNOLOGY_VERSION_FIELDS}
  query GetTechnology($id: ID!) {
    technology(id: $id) {
      ...TechnologyFields
      versions {
        ...TechnologyVersionFields
      }
      usedByServiceCount
    }
  }
`;

/**
 * List a technology's versions, optionally filtered to a single publish
 * status (e.g. `published` for a version picker, issue #TBD2).
 */
export const LIST_TECHNOLOGY_VERSIONS = gql`
  ${TECHNOLOGY_VERSION_FIELDS}
  query ListTechnologyVersions($technologyId: ID!, $status: TechnologyVersionStatus) {
    technologyVersions(technologyId: $technologyId, status: $status) {
      ...TechnologyVersionFields
    }
  }
`;

/**
 * Query to list technologies with server-side filtering, sorting, and offset pagination.
 * Pass page/pageSize/sortField/sortDirection for server-sort mode;
 * omit them to fall back to legacy cursor-based mode (first/after).
 */
export const LIST_TECHNOLOGIES = gql`
  ${TECHNOLOGY_FIELDS}
  query ListTechnologies(
    $filter: TechnologyFilter
    $first: Int
    $after: String
    $page: Int
    $pageSize: Int
    $sortField: String
    $sortDirection: String
  ) {
    technologies(
      filter: $filter
      first: $first
      after: $after
      page: $page
      pageSize: $pageSize
      sortField: $sortField
      sortDirection: $sortDirection
    ) {
      edges {
        node {
          ...TechnologyFields
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`;

export function useTechnology(id: string): QueryResult<{ technology: Technology | null }> {
  return useQuery(GET_TECHNOLOGY, {
    variables: { id },
    skip: !id,
  });
}

/**
 * List a technology's versions, optionally filtered to a single publish
 * status (issue #1283).
 */
export function useTechnologyVersions(
  technologyId: string,
  status?: TechnologyVersionStatus
): QueryResult<{ technologyVersions: TechnologyVersion[] }> {
  return useQuery(LIST_TECHNOLOGY_VERSIONS, {
    variables: { technologyId, status },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    skip: !technologyId,
  });
}

/**
 * Options for {@link useTechnologies} server-sort mode (issue #765).
 */
export interface UseTechnologiesOptions {
  filter?: TechnologyFilter;
  /** Legacy cursor-based: fetch at most this many records. Use for non-list contexts. */
  first?: number;
  after?: string;
  /** Server-sort+offset mode: current page (1-based). */
  page?: number;
  /** Server-sort+offset mode: rows per page. */
  pageSize?: number;
  /** Server-sort+offset mode: field to sort by. */
  sortField?: string;
  /** Server-sort+offset mode: 'asc' | 'desc'. */
  sortDirection?: string | null;
}

export function useTechnologies(
  filterOrFirst?: TechnologyFilter | number,
  first?: number
): QueryResult<{ technologies: TechnologyConnection }>;
export function useTechnologies(
  options: UseTechnologiesOptions
): QueryResult<{ technologies: TechnologyConnection }>;
export function useTechnologies(
  filterOrFirstOrOptions?: TechnologyFilter | number | UseTechnologiesOptions,
  first: number = 50
): QueryResult<{ technologies: TechnologyConnection }> {
  const variables = (() => {
    if (filterOrFirstOrOptions === undefined || filterOrFirstOrOptions === null) {
      return { first };
    }
    if (typeof filterOrFirstOrOptions === 'number') {
      return { first: filterOrFirstOrOptions };
    }
    if (
      'page' in filterOrFirstOrOptions ||
      'pageSize' in filterOrFirstOrOptions ||
      'sortField' in filterOrFirstOrOptions ||
      'sortDirection' in filterOrFirstOrOptions ||
      'after' in filterOrFirstOrOptions
    ) {
      const opts = filterOrFirstOrOptions as UseTechnologiesOptions;
      return {
        filter: opts.filter,
        first: opts.first,
        after: opts.after,
        page: opts.page,
        pageSize: opts.pageSize,
        sortField: opts.sortField,
        sortDirection: opts.sortDirection,
      };
    }
    // Legacy: called as useTechnologies(filter, first)
    return { filter: filterOrFirstOrOptions as TechnologyFilter, first };
  })();

  return useQuery(LIST_TECHNOLOGIES, {
    variables,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
}
